import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  csvToFaq,
  csvToProducts,
  emptyProduct,
  formatFaq,
  formatProduct,
  MAX_FAQ_PAIRS,
  MAX_PRODUCTS_PER_IMPORT,
  parseCsv,
  parseFaq,
  parseProduct,
  type FaqPair,
  type Product,
} from '@prakash-software/shared';
import { apiFetch } from '../../lib/api';
import {
  addKnowledgeSource,
  createKnowledge,
  deleteKnowledge,
  deleteKnowledgeSource,
  embeddingsErrorMessage,
  generateEntryQa,
  generateSourceQa,
  importKnowledgeCsv,
  knowledgeErrorMessage,
  listKnowledge,
  listKnowledgeSources,
  recrawlKnowledgeSource,
  updateKnowledge,
  uploadKnowledgePdf,
  type KnowledgeEntry,
  type KnowledgeInput,
  type KnowledgeKind,
  type KnowledgeSource,
} from '../../lib/knowledge-api';
import admin from './admin.module.css';
import styles from './KnowledgeBase.module.css';
import { confirmDialog } from '../../stores/confirm';

// RAG on/off + top-k live on the widget config; the KB section reads and
// PATCHes just those two fields so it stays self-contained (mirrors how
// CommandsSection owns its own data).
interface RagConfig {
  ragEnabled: boolean;
  ragTopK: number;
}

const TOPK_MIN = 1;
const TOPK_MAX = 20;
const MAX_CRAWL_PAGES = 200;
/** How often a running crawl's progress is re-read. */
const CRAWL_POLL_MS = 3000;

const KIND_LABEL: Record<KnowledgeKind, string> = {
  text: 'Text',
  md: 'MD',
  faq: 'FAQ',
  pdf: 'PDF',
  url: 'Page',
  product: 'Product',
};

function clampTopK(n: number): number {
  if (!Number.isFinite(n)) return TOPK_MIN;
  return Math.max(TOPK_MIN, Math.min(TOPK_MAX, Math.trunc(n)));
}

function formatUpdated(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const isRunning = (s: KnowledgeSource) => s.status === 'queued' || s.status === 'crawling';

function sourceStatus(s: KnowledgeSource): string {
  if (s.mode === 'page') {
    if (isRunning(s)) return 'Reading…';
    if (s.status === 'failed') return 'Could not read this page';
    return `Read ${formatUpdated(s.finishedAt)}`;
  }
  if (isRunning(s)) return `Crawling… ${s.pagesIndexed} pages indexed so far`;
  if (s.status === 'failed') return 'Crawl failed';
  const failed = s.pagesFailed > 0 ? ` · ${s.pagesFailed} unreachable` : '';
  return `${s.pagesIndexed} pages · crawled ${formatUpdated(s.finishedAt)}${failed}`;
}

// ─── Knowledge base section ────────────────────────────────────────────────
// Super-admin / site-admin manages the per-site RAG corpus: websites crawled
// into it, uploaded PDFs, and hand-written text / Markdown / FAQ entries, plus
// the site-wide RAG toggle and how many chunks to retrieve per reply.
export function KnowledgeBaseSection({ siteId }: { siteId: string }) {
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [sources, setSources] = useState<KnowledgeSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [rag, setRag] = useState<RagConfig | null>(null);
  const [ragSaving, setRagSaving] = useState(false);
  const [ragError, setRagError] = useState<string | null>(null);

  // Modal editor state. `editing` is the entry being edited, or 'new' for a
  // fresh entry, or null when the modal is closed.
  const [editing, setEditing] = useState<KnowledgeEntry | 'new' | null>(null);
  // A Q&A draft generated from an entry, opened in the editor for review.
  const [qaDraft, setQaDraft] = useState<{ title: string; content: string; sourceUrl: string | null } | null>(null);
  const [qaBusy, setQaBusy] = useState<string | null>(null);
  const [openSource, setOpenSource] = useState<string | null>(null);
  const [addingWebsite, setAddingWebsite] = useState(false);
  const [importingCsv, setImportingCsv] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [kindFilter, setKindFilter] = useState<KnowledgeKind | 'all'>('all');
  const [pdfBusy, setPdfBusy] = useState(false);
  const pdfInput = useRef<HTMLInputElement>(null);

  async function load(quiet = false) {
    if (!quiet) setLoading(true);
    try {
      const [list, sourceList] = await Promise.all([listKnowledge(siteId), listKnowledgeSources(siteId)]);
      setEntries(list);
      setSources(sourceList);
      setError(null);
    } catch {
      setError('Could not load the knowledge base.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }

  async function loadRag() {
    try {
      const { widgetConfig } = await apiFetch<{ widgetConfig: RagConfig }>(
        `/api/admin/sites/${siteId}/widget-config`,
      );
      setRag({
        ragEnabled: !!widgetConfig.ragEnabled,
        ragTopK: clampTopK(widgetConfig.ragTopK ?? 4),
      });
    } catch {
      // Non-fatal: entries can still be managed without the RAG toggle.
      setRag(null);
    }
  }

  useEffect(() => {
    void load();
    void loadRag();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  // While any crawl runs, re-read sources (and the pages they produce).
  const crawling = sources.some(isRunning);
  useEffect(() => {
    if (!crawling) return;
    const timer = window.setInterval(() => void load(true), CRAWL_POLL_MS);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crawling, siteId]);

  async function patchRag(patch: Partial<RagConfig>) {
    if (!rag) return;
    const next = { ...rag, ...patch };
    setRag(next);
    setRagSaving(true);
    setRagError(null);
    try {
      const { widgetConfig } = await apiFetch<{ widgetConfig: RagConfig }>(
        `/api/admin/sites/${siteId}/widget-config`,
        { method: 'PATCH', body: JSON.stringify(patch) },
      );
      setRag({
        ragEnabled: !!widgetConfig.ragEnabled,
        ragTopK: clampTopK(widgetConfig.ragTopK ?? next.ragTopK),
      });
    } catch {
      setRagError('Could not save RAG settings.');
      void loadRag();
    } finally {
      setRagSaving(false);
    }
  }

  async function remove(entry: KnowledgeEntry) {
    if (!(await confirmDialog({ title: `Delete “${entry.title}”?`, message: "This removes it from the AI's knowledge." }))) return;
    setError(null);
    try {
      await deleteKnowledge(siteId, entry.id);
      await load();
    } catch {
      setError('Delete failed.');
    }
  }

  async function removeSource(source: KnowledgeSource) {
    const pages = source.pagesIndexed === 1 ? '1 page' : `${source.pagesIndexed} pages`;
    if (!(await confirmDialog({ title: `Remove ${source.url} and its ${pages} from the AI's knowledge?`, confirmLabel: 'Remove' }))) return;
    setError(null);
    try {
      await deleteKnowledgeSource(siteId, source.id);
      await load();
    } catch {
      setError('Could not remove this website.');
    }
  }

  async function recrawl(source: KnowledgeSource) {
    setError(null);
    try {
      await recrawlKnowledgeSource(siteId, source.id);
      await load(true);
    } catch {
      setError('Could not start the crawl. It may already be running.');
    }
  }

  // AI drafts questions and answers from an entry; the admin reviews them in
  // the editor and saves them as a Q&A entry.
  async function makeQa(entry: KnowledgeEntry) {
    setQaBusy(entry.id);
    setError(null);
    try {
      const res = await generateEntryQa(siteId, entry.id);
      if (res.pairs.length === 0) {
        setNotice(`No questions and answers could be drawn from “${entry.title}”.`);
        return;
      }
      setQaDraft({ title: res.title, content: formatFaq(res.pairs), sourceUrl: res.sourceUrl });
    } catch (err) {
      setError(knowledgeErrorMessage(err) ?? 'Could not generate questions and answers.');
    } finally {
      setQaBusy(null);
    }
  }

  async function makeSourceQa(source: KnowledgeSource) {
    if (!(await confirmDialog({ title: `Turn every page of ${source.url} into questions and answers?`, message: 'This uses the AI provider once or more per page, in the background.', tone: 'default', confirmLabel: 'Generate Q&A' }))) return;
    setError(null);
    try {
      const n = await generateSourceQa(siteId, source.id);
      setNotice(`Generating Q&A for ${n} pages in the background. Each appears below as “Q&A: <page title>”; refresh in a minute.`);
    } catch {
      setError('Could not start generating Q&A.');
    }
  }

  async function uploadPdf(file: File | undefined) {
    if (!file) return;
    setPdfBusy(true);
    setError(null);
    try {
      await uploadKnowledgePdf(siteId, file);
      await load(true);
    } catch (err) {
      setError(
        knowledgeErrorMessage(err) ??
          embeddingsErrorMessage(err) ??
          `Could not add “${file.name}”. Check it is a PDF under 20 MB.`,
      );
    } finally {
      setPdfBusy(false);
      if (pdfInput.current) pdfInput.current.value = '';
    }
  }

  // Crawled pages are managed through their website, not listed one by one.
  const ownEntries = entries.filter((entry) => !entry.sourceId);
  const q = query.trim().toLowerCase();
  const shownEntries = ownEntries.filter(
    (e) =>
      (kindFilter === 'all' || e.kind === kindFilter) &&
      (!q || e.title.toLowerCase().includes(q) || e.content.toLowerCase().includes(q)),
  );
  const kindsPresent = [...new Set(ownEntries.map((e) => e.kind))];

  return (
    <section className={admin.section}>
      <div className={styles.headRow}>
        <div>
          <p className={admin.kicker}>Data sources</p>
          <h2 className={admin.title} style={{ marginBottom: 0 }}>
            What the AI knows about you.
          </h2>
        </div>
        <div className={styles.headActions}>
          <button type="button" className={admin.buttonGhost} onClick={() => setAddingWebsite(true)}>
            Add website or pages
          </button>
          <button type="button" className={admin.buttonGhost} onClick={() => setImportingCsv(true)}>
            Import CSV
          </button>
          <button
            type="button"
            className={admin.buttonGhost}
            disabled={pdfBusy}
            onClick={() => pdfInput.current?.click()}
          >
            {pdfBusy ? 'Reading PDF…' : 'Upload PDF'}
          </button>
          <input
            ref={pdfInput}
            type="file"
            accept="application/pdf,.pdf"
            hidden
            onChange={(e) => void uploadPdf(e.target.files?.[0])}
          />
          <button type="button" className={admin.button} onClick={() => setEditing('new')}>
            New entry →
          </button>
        </div>
      </div>

      <p style={{ marginTop: 0, color: 'var(--inic-ink-subtle)', fontSize: 'var(--inic-size-sm)' }}>
        Everything the assistant is trained on, in one place: your website (crawled whole, or the
        pages you choose), PDFs, questions and answers, products, and free text. Add, edit or delete
        any of it. Import Q&amp;A or a product catalogue from a CSV file. When retrieval is on, the
        assistant pulls the most relevant passages into each answer.
      </p>

      {rag && (
        <div className={styles.ragBar}>
          <label className={styles.toggleField}>
            <input
              type="checkbox"
              checked={rag.ragEnabled}
              disabled={ragSaving}
              onChange={(e) => void patchRag({ ragEnabled: e.target.checked })}
            />
            <span className={styles.toggleCopy}>
              <span className={admin.label} style={{ textTransform: 'none' }}>
                Answer visitors from these data sources
              </span>
              <span className={admin.hint}>
                Off = the assistant ignores everything below (and no read-more links or suggestions). Entries
                can be managed either way.
              </span>
            </span>
          </label>

          <label className={styles.topkField}>
            <span className={admin.label}>Chunks to retrieve (top-k)</span>
            <input
              className={`${admin.input} ${styles.topkInput}`}
              type="number"
              min={TOPK_MIN}
              max={TOPK_MAX}
              value={rag.ragTopK}
              disabled={ragSaving || !rag.ragEnabled}
              onChange={(e) => setRag({ ...rag, ragTopK: clampTopK(Number(e.target.value)) })}
              onBlur={(e) => {
                const v = clampTopK(Number(e.target.value));
                if (v !== rag.ragTopK) void patchRag({ ragTopK: v });
              }}
            />
            <span className={admin.hint}>Between {TOPK_MIN} and {TOPK_MAX}.</span>
          </label>

          <span className={styles.ragStatus}>
            {ragSaving ? 'Saving…' : ragError ? '' : 'Saved automatically'}
          </span>
          {ragError && <span className={admin.error} style={{ margin: 0 }}>{ragError}</span>}
        </div>
      )}

      {rag && !rag.ragEnabled && (ownEntries.length > 0 || sources.length > 0) && (
        <p className={admin.notice} role="status" style={{ display: 'flex', alignItems: 'center', gap: 'var(--inic-space-3)' }}>
          <span style={{ flex: 1 }}>
            The assistant isn’t using these data sources yet, so it can’t answer from them, link pages or collect
            suggestions.
          </span>
          <button type="button" className={admin.button} disabled={ragSaving} onClick={() => void patchRag({ ragEnabled: true })}>
            Turn on
          </button>
        </p>
      )}
      {error && <p className={admin.error}>{error}</p>}
      {notice && (
        <p className={admin.notice} role="status">
          {notice}
        </p>
      )}

      {sources.length > 0 && (
        <>
          <p className={styles.subhead}>Websites</p>
          <div className={styles.list}>
            {sources.map((source) => (
              <div key={source.id} className={openSource === source.id ? `${styles.row} ${styles.rowOpen}` : styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>
                    <span className={admin.badge}>{source.mode === 'page' ? 'Page' : 'Site'}</span>
                    <span className={styles.rowTitleText}>{source.url}</span>
                  </span>
                  <span className={`${styles.rowMeta} ${source.status === 'failed' ? styles.statusFailed : ''}`}>
                    {sourceStatus(source)}
                  </span>
                  {source.errors.length > 0 && !isRunning(source) && (
                    <details className={styles.crawlErrors}>
                      <summary>{source.errors[0]}</summary>
                      {source.errors.length > 1 && (
                        <ul>
                          {source.errors.slice(1).map((e) => (
                            <li key={e}>{e}</li>
                          ))}
                        </ul>
                      )}
                    </details>
                  )}
                </div>
                <div className={admin.actions}>
                  <button
                    type="button"
                    className={admin.buttonGhost}
                    onClick={() => setOpenSource(openSource === source.id ? null : source.id)}
                    aria-expanded={openSource === source.id}
                  >
                    Pages ({entries.filter((e) => e.sourceId === source.id).length})
                  </button>
                  <button
                    type="button"
                    className={admin.buttonGhost}
                    disabled={isRunning(source) || source.pagesIndexed === 0}
                    onClick={() => void makeSourceQa(source)}
                  >
                    Generate Q&amp;A
                  </button>
                  <button
                    type="button"
                    className={admin.buttonGhost}
                    disabled={isRunning(source)}
                    onClick={() => void recrawl(source)}
                  >
                    {source.mode === 'page' ? 'Read again' : 'Crawl again'}
                  </button>
                  <button
                    type="button"
                    className={admin.buttonDanger}
                    onClick={() => void removeSource(source)}
                  >
                    delete
                  </button>
                </div>
                {openSource === source.id && (
                  <div className={`${styles.list} ${styles.pageList}`}>
                    {entries
                      .filter((e) => e.sourceId === source.id)
                      .map((page) => (
                        <div key={page.id} className={styles.row}>
                          <div className={styles.rowMain}>
                            <span className={styles.rowTitle}>
                              <span className={admin.badge}>{KIND_LABEL[page.kind]}</span>
                              <span className={styles.rowTitleText}>{page.title}</span>
                            </span>
                            <span className={styles.rowMeta}>
                              {page.sourceUrl && (
                                <a href={page.sourceUrl} target="_blank" rel="noopener noreferrer">
                                  {page.sourceUrl}
                                </a>
                              )}
                            </span>
                          </div>
                          <div className={admin.actions}>
                            <button type="button" className={admin.buttonGhost} onClick={() => setEditing(page)}>
                              View / edit
                            </button>
                            <button
                              type="button"
                              className={admin.buttonGhost}
                              disabled={qaBusy !== null}
                              onClick={() => void makeQa(page)}
                            >
                              {qaBusy === page.id ? 'Generating…' : 'Make Q&A'}
                            </button>
                            <button type="button" className={admin.buttonDanger} onClick={() => void remove(page)}>
                              delete
                            </button>
                          </div>
                        </div>
                      ))}
                    {entries.every((e) => e.sourceId !== source.id) && (
                      <div className={styles.empty}>No pages read yet.</div>
                    )}
                    <p className={admin.hint}>
                      Editing a page keeps your version: it leaves the website and “Crawl again” won’t overwrite it.
                      A deleted page comes back on the next crawl.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {sources.length > 0 && <p className={styles.subhead}>Entries</p>}
      {ownEntries.length > 5 && (
        <div style={{ display: 'flex', gap: 'var(--inic-space-2)', margin: '0 0 var(--inic-space-3)' }}>
          <input
            className={admin.input}
            style={{ flex: 1, minWidth: 0 }}
            type="search"
            value={query}
            placeholder={`Search ${ownEntries.length} entries…`}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search entries"
          />
          <select
            className={admin.select}
            style={{ width: 'auto' }}
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as KnowledgeKind | 'all')}
            aria-label="Filter by type"
          >
            <option value="all">All types</option>
            {kindsPresent.map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </div>
      )}
      {loading ? (
        <div className={styles.empty}>Loading…</div>
      ) : ownEntries.length === 0 ? (
        <div className={styles.empty}>
          {sources.length > 0
            ? 'No hand-written entries or PDFs yet.'
            : 'No knowledge yet. Add your website, a PDF, or a first entry to give the AI something to work from.'}
        </div>
      ) : shownEntries.length === 0 ? (
        <div className={styles.empty}>No entries match.</div>
      ) : (
        <div className={styles.list}>
          {shownEntries.map((entry) => (
            <div key={entry.id} className={styles.row}>
              <div className={styles.rowMain}>
                <span className={styles.rowTitle}>
                  <span className={admin.badge}>{KIND_LABEL[entry.kind]}</span>
                  <span className={styles.rowTitleText}>{entry.title}</span>
                </span>
                <span className={styles.rowMeta}>
                  Updated {formatUpdated(entry.updatedAt)}
                  {entry.kind === 'faq' && ` · ${parseFaq(entry.content).length} questions`}
                  {entry.sourceUrl && (
                    <>
                      {' · '}
                      <a href={entry.sourceUrl} target="_blank" rel="noopener noreferrer">
                        {entry.sourceUrl}
                      </a>
                    </>
                  )}
                </span>
              </div>
              <div className={admin.actions}>
                <button
                  type="button"
                  className={admin.buttonGhost}
                  onClick={() => setEditing(entry)}
                >
                  {entry.kind === 'pdf' ? 'Edit text' : 'Edit'}
                </button>
                {entry.kind !== 'faq' && entry.kind !== 'product' && (
                  <button
                    type="button"
                    className={admin.buttonGhost}
                    disabled={qaBusy !== null}
                    onClick={() => void makeQa(entry)}
                    title="Let the AI draft questions and answers from this entry; review them before saving"
                  >
                    {qaBusy === entry.id ? 'Generating…' : 'Make Q&A'}
                  </button>
                )}
                <button
                  type="button"
                  className={admin.buttonDanger}
                  onClick={() => void remove(entry)}
                >
                  delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <KnowledgeEditor
          siteId={siteId}
          entry={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      {qaDraft && (
        <KnowledgeEditor
          siteId={siteId}
          entry={null}
          draft={{ kind: 'faq', ...qaDraft }}
          onClose={() => setQaDraft(null)}
          onSaved={() => {
            setQaDraft(null);
            setNotice('Questions and answers saved.');
            void load();
          }}
        />
      )}

      {importingCsv && (
        <CsvImportDialog
          siteId={siteId}
          onClose={() => setImportingCsv(false)}
          onImported={(message) => {
            setImportingCsv(false);
            setNotice(message);
            void load(true);
          }}
        />
      )}

      {addingWebsite && (
        <WebsiteDialog
          siteId={siteId}
          onClose={() => setAddingWebsite(false)}
          onAdded={() => {
            setAddingWebsite(false);
            void load(true);
          }}
        />
      )}
    </section>
  );
}

// ─── Add website / pages modal ─────────────────────────────────────────────
// Either crawl a whole website from one address (sitemap + links), or read
// just the pages listed, one per line.
function WebsiteDialog({
  siteId,
  onClose,
  onAdded,
}: {
  siteId: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [mode, setMode] = useState<'site' | 'page'>('site');
  const [url, setUrl] = useState('');
  const [pages, setPages] = useState('');
  const [maxPages, setMaxPages] = useState(25);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pageList = [...new Set(pages.split(/\s+/).map((u) => u.trim()).filter(Boolean))];
  const ready = mode === 'site' ? Boolean(url.trim()) : pageList.length > 0 && pageList.length <= 50;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (saving || !ready) return;
    setSaving(true);
    setError(null);
    try {
      if (mode === 'site') {
        await addKnowledgeSource(siteId, url.trim(), maxPages, 'site');
      } else {
        const failed: string[] = [];
        for (const page of pageList) {
          try {
            await addKnowledgeSource(siteId, page, 1, 'page');
          } catch (err) {
            failed.push(`${page}: ${knowledgeErrorMessage(err) ?? 'could not be added'}`);
          }
        }
        if (failed.length) {
          setError(`${pageList.length - failed.length} added. Not added:\n${failed.join('\n')}`);
          if (failed.length < pageList.length) onAdded();
          return;
        }
      }
      onAdded();
    } catch (err) {
      setError(knowledgeErrorMessage(err) ?? 'Could not add this website. Check the address and try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={admin.modalBackdrop} role="dialog" aria-modal="true" aria-label="Add website or pages" onClick={onClose}>
      <form className={admin.modal} style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <h2 className={admin.modalTitle}>Add website or pages</h2>

        <div className={admin.field} role="radiogroup" aria-label="What to read">
          <label className={admin.checkRow}>
            <input type="radio" name="kb-mode" checked={mode === 'site'} onChange={() => setMode('site')} />
            <span className={admin.checkRowText}>
              <span className={admin.label} style={{ textTransform: 'none' }}>Whole website</span>
              <span className={admin.hint}>Enter the domain; its pages are found through the sitemap and links.</span>
            </span>
          </label>
          <label className={admin.checkRow}>
            <input type="radio" name="kb-mode" checked={mode === 'page'} onChange={() => setMode('page')} />
            <span className={admin.checkRowText}>
              <span className={admin.label} style={{ textTransform: 'none' }}>Specific pages</span>
              <span className={admin.hint}>Only the addresses you list, one per line (up to 50).</span>
            </span>
          </label>
        </div>

        {mode === 'site' ? (
          <>
            <label className={admin.field}>
              <span className={admin.label}>Website address</span>
              <input
                className={admin.input}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.example.com"
                inputMode="url"
                autoFocus
              />
              <span className={admin.hint}>
                Pages on this site only. Pages its robots.txt excludes are skipped; linked PDFs are included.
              </span>
            </label>
            <label className={admin.field}>
              <span className={admin.label}>Pages to index (at most)</span>
              <input
                className={admin.input}
                type="number"
                min={1}
                max={MAX_CRAWL_PAGES}
                value={maxPages}
                onChange={(e) => setMaxPages(Math.max(1, Math.min(MAX_CRAWL_PAGES, Math.trunc(Number(e.target.value) || 1))))}
              />
              <span className={admin.hint}>Between 1 and {MAX_CRAWL_PAGES}. The crawl runs in the background.</span>
            </label>
          </>
        ) : (
          <label className={admin.field}>
            <span className={admin.label}>Page addresses</span>
            <textarea
              className={admin.input}
              rows={6}
              value={pages}
              onChange={(e) => setPages(e.target.value)}
              placeholder={'https://www.example.com/pricing\nhttps://www.example.com/shipping'}
              autoFocus
            />
            <span className={admin.hint}>
              {pageList.length > 50 ? 'At most 50 at a time.' : `${pageList.length} page${pageList.length === 1 ? '' : 's'}`}
            </span>
          </label>
        )}

        {error && (
          <p className={styles.embedErr} style={{ whiteSpace: 'pre-line' }}>
            {error}
          </p>
        )}

        <div className={admin.modalActions}>
          <button type="button" className={admin.buttonGhost} onClick={onClose} disabled={saving}>
            {error ? 'Close' : 'Cancel'}
          </button>
          <button type="submit" className={admin.button} disabled={saving || !ready}>
            {saving ? 'Starting…' : mode === 'site' ? 'Crawl website →' : 'Read pages →'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── CSV import modal ──────────────────────────────────────────────────────
// Q&A (question, answer) or a product catalogue (name, price, url,
// description, sku, category, and any other columns). The file is parsed here
// too, so the admin sees what will be imported before sending it.
function CsvImportDialog({
  siteId,
  onClose,
  onImported,
}: {
  siteId: string;
  onClose: () => void;
  onImported: (message: string) => void;
}) {
  const [type, setType] = useState<'faq' | 'products'>('faq');
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<string[][] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = rows
    ? type === 'faq'
      ? csvToFaq(rows)
      : csvToProducts(rows)
    : null;
  const count = preview?.items.length ?? 0;
  const problem =
    preview && 'missingHeader' in preview && preview.missingHeader
      ? 'The first row must name the columns, including “name” (or “title”).'
      : type === 'products' && count > MAX_PRODUCTS_PER_IMPORT
        ? `At most ${MAX_PRODUCTS_PER_IMPORT} products at a time; split the file.`
        : rows && count === 0
          ? 'Nothing to import in this file.'
          : null;

  async function pick(f: File | undefined) {
    setFile(f ?? null);
    setError(null);
    setRows(f ? parseCsv(await f.text()) : null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!file || saving || problem) return;
    setSaving(true);
    setError(null);
    try {
      const res = await importKnowledgeCsv(siteId, file, type);
      const skipped = res.skipped.length ? `; ${res.skipped.length} rows skipped` : '';
      onImported(
        type === 'faq'
          ? `Imported ${res.items} questions and answers${skipped}.`
          : `Imported ${res.items} products${skipped}.`,
      );
    } catch (err) {
      setError(knowledgeErrorMessage(err) ?? embeddingsErrorMessage(err) ?? 'The import failed. Check the file and try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={admin.modalBackdrop} role="dialog" aria-modal="true" aria-label="Import CSV" onClick={onClose}>
      <form className={admin.modal} style={{ maxWidth: 600 }} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <h2 className={admin.modalTitle}>Import from a CSV file</h2>

        <label className={admin.field}>
          <span className={admin.label}>The file holds</span>
          <select className={admin.select} value={type} onChange={(e) => setType(e.target.value as 'faq' | 'products')}>
            <option value="faq">Questions and answers</option>
            <option value="products">Products</option>
          </select>
          <span className={admin.hint}>
            {type === 'faq'
              ? 'Columns “question” and “answer” (or just the first two columns).'
              : 'A header row with “name” (or “title”), and any of “price”, “url”, “description”, “sku”, “category”, “availability”. Other columns are kept as product details. Shopify and WooCommerce exports work as they are.'}
          </span>
        </label>

        <label className={admin.field}>
          <span className={admin.label}>CSV file</span>
          <input
            className={admin.input}
            type="file"
            accept=".csv,text/csv,.tsv,.txt"
            onChange={(e) => void pick(e.target.files?.[0])}
          />
        </label>

        {preview && !problem && (
          <p className={admin.notice} role="status">
            {type === 'faq' ? `${count} questions and answers` : `${count} products`} ready to import
            {preview.skipped.length > 0 &&
              `; ${preview.skipped.length} rows will be skipped (row ${preview.skipped
                .slice(0, 3)
                .map((s) => `${s.row}: ${s.reason}`)
                .join(', row ')}${preview.skipped.length > 3 ? ', …' : ''})`}
            .
          </p>
        )}
        {(problem || error) && <p className={styles.embedErr}>{problem ?? error}</p>}

        <div className={admin.modalActions}>
          <button type="button" className={admin.buttonGhost} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className={admin.button} disabled={!file || saving || !!problem}>
            {saving ? 'Importing…' : 'Import →'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Add / edit modal ──────────────────────────────────────────────────────
// One editor for create and update. Text and Markdown get a large monospace
// textarea; FAQ gets a list of question/answer pairs, stored as "Q: / A:" text
// (see shared/knowledge/faq.ts). A 502/503 from the embeddings gateway is
// surfaced inline (via embeddingsErrorMessage) so the admin knows to fix the key.
type EditableKind = KnowledgeInput['kind'];

function KnowledgeEditor({
  siteId,
  entry,
  draft,
  onClose,
  onSaved,
}: {
  siteId: string;
  entry: KnowledgeEntry | null;
  /** Prefill for a new entry (AI-drafted Q&A); saved with its source URL. */
  draft?: { kind: EditableKind; title: string; content: string; sourceUrl: string | null };
  onClose: () => void;
  onSaved: () => void;
}) {
  const initialKind: EditableKind = entry ? entry.kind : (draft?.kind ?? 'text');
  const [title, setTitle] = useState(entry?.title ?? draft?.title ?? '');
  const [kind, setKind] = useState<EditableKind>(initialKind);
  const [content, setContent] = useState(entry?.content ?? draft?.content ?? '');
  const [pairs, setPairs] = useState<FaqPair[]>(() => {
    const parsed = initialKind === 'faq' ? parseFaq(entry?.content ?? draft?.content ?? '') : [];
    return parsed.length ? parsed : [{ question: '', answer: '' }];
  });
  const [product, setProduct] = useState<Product>(() =>
    initialKind === 'product' && entry
      ? { ...parseProduct(entry.content), url: entry.sourceUrl ?? parseProduct(entry.content).url }
      : emptyProduct(),
  );
  const setProductField = (key: keyof Omit<Product, 'details'>, value: string) =>
    setProduct((p) => ({ ...p, [key]: value }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function changeKind(next: EditableKind) {
    // Carry the content across: text pasted as Q:/A: becomes pairs, and pairs
    // become their text form.
    if (next === 'faq' && kind !== 'faq') {
      const parsed = parseFaq(content);
      if (parsed.length) setPairs(parsed);
    } else if (next !== 'faq' && kind === 'faq') {
      setContent(formatFaq(pairs.filter((p) => p.question.trim() && p.answer.trim())));
    }
    setKind(next);
  }

  function updatePair(index: number, patch: Partial<FaqPair>) {
    setPairs((current) => current.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    const t = kind === 'product' ? product.name.trim() : title.trim();
    const complete = pairs.filter((p) => p.question.trim() && p.answer.trim());
    const body = kind === 'faq' ? formatFaq(complete) : kind === 'product' ? formatProduct(product) : content;
    if (!t || !body.trim()) {
      setError(
        kind === 'faq'
          ? 'Add a title and at least one question with its answer.'
          : kind === 'product'
            ? 'A product needs a name.'
            : 'Title and content are both required.',
      );
      return;
    }
    if (kind === 'product' && product.url.trim() && !/^https?:\/\//i.test(product.url.trim())) {
      setError('The product link must start with http:// or https://.');
      return;
    }
    setSaving(true);
    setError(null);
    const input: KnowledgeInput = {
      kind,
      title: t,
      content: body,
      ...(kind === 'product' ? { sourceUrl: product.url.trim() || null } : {}),
      ...(!entry && draft?.sourceUrl && kind !== 'product' ? { sourceUrl: draft.sourceUrl } : {}),
    };
    try {
      if (entry) {
        await updateKnowledge(siteId, entry.id, input);
      } else {
        await createKnowledge(siteId, input);
      }
      onSaved();
    } catch (err) {
      // Prefer the embeddings-gateway message when that's the cause.
      setError(embeddingsErrorMessage(err) ?? 'Could not save this entry. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className={admin.modalBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={entry ? 'Edit knowledge entry' : 'New knowledge entry'}
      onClick={onClose}
    >
      <form
        className={admin.modal}
        style={{ maxWidth: 720, maxHeight: '88vh' }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h2 className={admin.modalTitle}>{entry ? 'Edit entry' : draft ? 'Review the generated Q&A' : 'New entry'}</h2>
        {draft && !entry && (
          <p className={admin.hint} style={{ margin: 0 }}>
            Drafted by the AI from the page. Check each answer, fix or remove any that are wrong, then save.
          </p>
        )}
        {entry?.sourceId && (
          <p className={admin.hint} style={{ margin: 0 }}>
            Saving your changes takes this page out of its website, so “Crawl again” won’t overwrite them.
          </p>
        )}

        {kind !== 'product' && (
          <label className={admin.field}>
            <span className={admin.label}>Title</span>
            <input
              className={admin.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Refund policy"
              autoFocus
            />
          </label>
        )}

        <label className={admin.field}>
          <span className={admin.label}>Format</span>
          <select
            className={admin.select}
            value={kind}
            disabled={kind === 'pdf' || kind === 'url'}
            onChange={(e) => changeKind(e.target.value as EditableKind)}
          >
            <option value="text">Raw text</option>
            <option value="md">Markdown</option>
            <option value="faq">Questions and answers</option>
            <option value="product">Product</option>
            {kind === 'pdf' && <option value="pdf">Text extracted from a PDF</option>}
            {kind === 'url' && <option value="url">Text read from a web page</option>}
          </select>
        </label>

        {kind === 'product' ? (
          <div className={admin.form}>
            {(
              [
                ['name', 'Product name', 'Trail running shoe'],
                ['price', 'Price', '₹4,999'],
                ['sku', 'SKU / code', 'TRS-42'],
                ['category', 'Category', 'Shoes'],
                ['availability', 'Availability', 'In stock'],
                ['url', 'Product page', 'https://shop.example.com/trail-shoe'],
              ] as const
            ).map(([key, label, ph]) => (
              <label key={key} className={admin.field} style={key === 'name' || key === 'url' ? { gridColumn: '1 / -1' } : undefined}>
                <span className={admin.label}>{label}</span>
                <input
                  className={admin.input}
                  value={product[key]}
                  placeholder={ph}
                  onChange={(e) => setProductField(key, e.target.value)}
                  {...(key === 'name' ? { autoFocus: true } : {})}
                />
              </label>
            ))}
            <label className={admin.field} style={{ gridColumn: '1 / -1' }}>
              <span className={admin.label}>Description</span>
              <textarea
                className={admin.input}
                rows={5}
                value={product.description}
                placeholder="What it is, who it's for, sizes, materials, care…"
                onChange={(e) => setProductField('description', e.target.value)}
              />
              <span className={admin.hint}>
                Answers about this product link its page when “Read more” links are on.
              </span>
            </label>
          </div>
        ) : kind === 'faq' ? (
          <div className={admin.field}>
            <span className={admin.label}>Questions and answers</span>
            <div className={styles.faqList}>
              {pairs.map((pair, index) => (
                <div key={index} className={styles.faqPair}>
                  <input
                    className={admin.input}
                    value={pair.question}
                    onChange={(e) => updatePair(index, { question: e.target.value })}
                    placeholder="Do you offer refunds?"
                    aria-label={`Question ${index + 1}`}
                  />
                  <button
                    type="button"
                    className={admin.buttonGhost}
                    disabled={pairs.length === 1}
                    onClick={() => setPairs((current) => current.filter((_, i) => i !== index))}
                    aria-label={`Remove question ${index + 1}`}
                  >
                    Remove
                  </button>
                  <textarea
                    className={admin.input}
                    value={pair.answer}
                    onChange={(e) => updatePair(index, { answer: e.target.value })}
                    placeholder="Yes — within 30 days of delivery."
                    aria-label={`Answer ${index + 1}`}
                  />
                </div>
              ))}
            </div>
            <button
              type="button"
              className={admin.buttonGhost}
              style={{ alignSelf: 'flex-start' }}
              disabled={pairs.length >= MAX_FAQ_PAIRS}
              onClick={() => setPairs((current) => [...current, { question: '', answer: '' }])}
            >
              Add question
            </button>
            <span className={admin.hint}>Each answer is indexed with its question, so the AI finds the exact answer.</span>
          </div>
        ) : (
          <label className={admin.field}>
            <span className={admin.label}>Content</span>
            <textarea
              className={`${admin.input} ${styles.contentArea}`}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Paste the document, FAQ, or policy the AI should be able to draw on…"
            />
          </label>
        )}

        {error && <p className={styles.embedErr}>{error}</p>}

        <div className={admin.modalActions}>
          <button type="button" className={admin.buttonGhost} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className={admin.button} disabled={saving}>
            {saving ? 'Saving…' : entry ? 'Save changes →' : 'Add entry →'}
          </button>
        </div>
      </form>
    </div>
  );
}
